import { useCallback, useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, DollarSign, FileText, Calendar, Award, Users, ChevronRight } from "lucide-react";
import PageLayout from "../../components/layout/PageLayout";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import { Link } from "react-router-dom";

const growthData = [
    { m: "Sep", followers: 42000 },
    { m: "Oct", followers: 48000 },
    { m: "Nov", followers: 55000 },
    { m: "Dec", followers: 63000 },
    { m: "Jan", followers: 71000 },
    { m: "Feb", followers: 82000 },
];

export default function AthleteDashboard() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [deals, setDeals] = useState([]);
    const [content, setContent] = useState([]);

    const load = useCallback(() => {
        api.get("/athlete/summary").then(r => setSummary(r.data)).catch(() => {});
        api.get("/deals").then(r => setDeals(r.data)).catch(() => {});
        api.get("/content").then(r => setContent(r.data)).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!summary) return <PageLayout><div className="pt-40 text-center text-white/40">Loading…</div></PageLayout>;

    const a = summary.athlete;

    return (
        <PageLayout>
            <section className="pt-28 pb-8 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="flex flex-wrap justify-between items-end gap-4">
                        <div>
                            <div className="section-label mb-3">Athlete Dashboard</div>
                            <h1 className="font-display text-4xl md:text-5xl text-white tracking-tight" data-testid="athlete-dashboard-heading">
                                WELCOME, <span className="text-[#d4af37]">{user.name.split(" ")[0].toUpperCase()}</span>
                            </h1>
                            <div className="text-white/50 text-sm mt-2">{a ? `${a.school} · ${a.position} · #${a.jersey}` : "Profile pending"}</div>
                        </div>
                        {a && <Link to={`/athletes/${a.id}`} className="btn-outline" data-testid="view-public-profile">View Public Profile <ChevronRight size={14} /></Link>}
                    </div>
                </div>
            </section>

            {!summary.has_profile ? (
                <section className="py-16">
                    <div className="max-w-2xl mx-auto px-5 text-center surface p-10" data-testid="no-profile">
                        <div className="font-display text-3xl text-[#d4af37]">Profile Pending</div>
                        <p className="text-white/60 mt-4">Your Digital Beast profile is being reviewed by our management team. Once approved you'll get access to deals, content and analytics.</p>
                    </div>
                </section>
            ) : (
                <section className="py-10">
                    <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-4 gap-4">
                        {[
                            { key: "active-deals", icon: Award, l: "Active Deals", v: summary.active_deals },
                            { key: "paid-earnings", icon: DollarSign, l: "Paid Earnings", v: `$${summary.paid_earnings.toLocaleString()}` },
                            { key: "pending-payments", icon: DollarSign, l: "Pending Payments", v: `$${summary.pending_payments.toLocaleString()}` },
                            { key: "scheduled-content", icon: Calendar, l: "Scheduled Content", v: summary.content_scheduled },
                        ].map(({key, icon: I, l, v}) => (
                            <div key={key} className="surface p-6" data-testid={`athlete-kpi-${key}`}>
                                <I className="text-[#d4af37]" size={22} strokeWidth={1.5} />
                                <div className="font-display text-4xl text-white mt-4 leading-none">{v}</div>
                                <div className="text-white/40 text-xs uppercase tracking-widest mt-3">{l}</div>
                            </div>
                        ))}
                    </div>

                    <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-6 grid lg:grid-cols-3 gap-4">
                        <div className="surface p-6 lg:col-span-2" data-testid="athlete-growth">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <div className="section-label mb-2">Audience Growth</div>
                                    <div className="font-display text-2xl text-white">Follower Trajectory</div>
                                </div>
                                <TrendingUp className="text-[#d4af37]" />
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <LineChart data={growthData}>
                                    <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                                    <XAxis dataKey="m" stroke="#555" tick={{ fill: "#888", fontSize: 12 }} />
                                    <YAxis stroke="#555" tick={{ fill: "#888", fontSize: 12 }} tickFormatter={(v) => `${v/1000}K`} />
                                    <Tooltip contentStyle={{ background: "#0f0f11", border: "1px solid #333", color: "#fff" }} />
                                    <Line type="monotone" dataKey="followers" stroke="#d4af37" strokeWidth={2} dot={{ fill: "#d4af37", r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="surface p-6" data-testid="athlete-content-status">
                            <div className="section-label mb-3">Content Awaiting Approval</div>
                            <div className="font-display text-5xl text-[#d4af37]">{summary.content_pending_approval}</div>
                            <div className="text-white/40 text-xs uppercase tracking-widest mt-3">posts ready for your review</div>
                            <div className="mt-6 space-y-3">
                                {content.slice(0, 3).map(c => (
                                    <div key={c.id} className="border-t border-white/10 pt-3">
                                        <div className="text-white text-sm">{c.title}</div>
                                        <div className="text-white/40 text-xs uppercase tracking-widest mt-1">{c.status}</div>
                                    </div>
                                ))}
                                {content.length === 0 && <div className="text-white/40 text-xs">No content scheduled yet.</div>}
                            </div>
                        </div>
                    </div>

                    <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-6">
                        <div className="section-label mb-5">Deal Center</div>
                        <div className="surface overflow-x-auto" data-testid="athlete-deals-table">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-white/40 text-xs uppercase tracking-widest">
                                        <th className="text-left p-4">Brand</th>
                                        <th className="text-left p-4">Campaign</th>
                                        <th className="text-right p-4">Compensation</th>
                                        <th className="text-right p-4">Athlete Net</th>
                                        <th className="text-left p-4">Status</th>
                                        <th className="text-left p-4">Payment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deals.length === 0 ? (
                                        <tr><td colSpan={6} className="p-8 text-center text-white/40">No deals yet. Management will populate this as opportunities come in.</td></tr>
                                    ) : deals.map(d => (
                                        <tr key={d.id} className="border-t border-white/10">
                                            <td className="p-4 text-white">{d.brand}</td>
                                            <td className="p-4 text-white/70">{d.campaign}</td>
                                            <td className="p-4 text-right text-white">${d.compensation.toLocaleString()}</td>
                                            <td className="p-4 text-right text-[#d4af37]">${d.athlete_net.toLocaleString()}</td>
                                            <td className="p-4"><span className="chip">{d.status}</span></td>
                                            <td className="p-4"><span className="chip">{d.payment_status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}
        </PageLayout>
    );
}
